import { z } from 'zod'
import { Temporal } from '@js-temporal/polyfill'
import { ContractDTO, mapContractToDTO, mapDTOToContract } from './ContractDTO'
import { CompanyContactDTO, mapCompanyContactToDTO, mapDTOToCompanyContact } from './CompanyContactDTO'
import { CompanyAddressDTO, mapCompanyAddressToDTO, mapDTOToCompanyAddress } from './CompanyAddressDTO'
import { DocumentType } from '~/domain/shared/document/models/Document'
import { ContactDTO, mapContactToDTO, mapDTOToContact } from '~/domain/dashboard/customer/gateways/DTOs/ContactDTO'
import { type Company } from '~/domain/shared/company/models/Company'
import {
  CompanyDocumentDTO,
  mapCompanyDocumentToDTO,
  mapDTOToCompanyDocument
} from '~/domain/dashboard/company/gateways/DTOs/CompanyDocumentDTO'
import { CnaeDTO, mapCnaeToDTO, mapDTOToCnae } from '~/domain/dashboard/company/cnae/gateways/DTOs/CnaeDTO'
import {
  TypeOfBusinessDTO,
  mapTypeOfBusinessToDTO,
  mapDTOToTypeOfBusiness
} from '~/domain/dashboard/company/gateways/DTOs/TypeOfBusinessDTO'

export const CompanyDTO = z.object({
  id: z.number().optional(),
  code: z.string().nullish(),
  is_main: z.boolean().nullish(),
  address: CompanyAddressDTO.array().nullish(),
  name: z.string().nullish(),
  image_url: z.string().nullish(),
  company_name: z.string().nullish(),
  founding_date: z.string().nullish(),
  cnae: CnaeDTO.nullish(),
  cnae_id: z.number().nullish(),
  contacts: ContactDTO.array().nullish(),
  documents: CompanyDocumentDTO.array().nullish(),
  type_of_business: TypeOfBusinessDTO.nullish(),
  owner_contact: CompanyContactDTO.nullish(),
  financial_contact: CompanyContactDTO.nullish(),
  clone_account_plan_from_company_id: z.number().nullish(),
  certificate_expiration_date: z.string().nullish(),
  internal_user: z.boolean().nullish(),
  contract: ContractDTO.nullish()
})

const CompanyDTORequest = CompanyDTO.extend({
  type_of_business: z.string().nullish()
})

export type CompanyDTO = z.infer<typeof CompanyDTO>
export type CompanyDTORequest = z.infer<typeof CompanyDTORequest>

export function mapCompanyToDTO (model: Company): CompanyDTORequest {
  const cnpjDocument = model.documents.find(d => d.type === DocumentType.CNPJ)
  const documents = cnpjDocument ? [cnpjDocument] : model.documents.filter(d => d.value != null && d.value !== '')

  return {
    id: model.id,
    code: model.code,
    name: model.name,
    image_url: model.logo,
    company_name: model.companyName,
    founding_date: model.foundingDate?.toString(),
    cnae: model.cnae ? mapCnaeToDTO(model.cnae) : undefined,
    cnae_id: model.cnae?.id,
    address: model.addresses?.map(mapCompanyAddressToDTO),
    contacts: model.contacts?.map(mapContactToDTO),
    documents: documents.map(mapCompanyDocumentToDTO),
    type_of_business: model.typeOfBusiness ? mapTypeOfBusinessToDTO(model.typeOfBusiness).type : undefined,
    owner_contact: model.ownerContact ? mapCompanyContactToDTO(model.ownerContact) : undefined,
    financial_contact: model.financialContact ? mapCompanyContactToDTO(model.financialContact) : undefined,
    clone_account_plan_from_company_id: model.cloneAccountPlanFromCompany?.id,
    certificate_expiration_date: model.certificateExpirationDate?.toString(),
    contract: model.contract ? mapContractToDTO(model.contract) : undefined
  }
}

export function mapDTOToCompany (dto: CompanyDTO): Company {
  return {
    id: dto.id,
    code: dto.code ?? undefined,
    isMain: dto.is_main ?? undefined,
    name: dto.name ?? '',
    logo: dto.image_url ?? undefined,
    companyName: dto.company_name ?? undefined,
    foundingDate: dto.founding_date ? Temporal.PlainDate.from(dto.founding_date) : undefined,
    cnae: dto.cnae ? mapDTOToCnae(dto.cnae) : undefined,
    typeOfBusiness: dto.type_of_business ? mapDTOToTypeOfBusiness(dto.type_of_business) : undefined,
    addresses: dto.address?.map(mapDTOToCompanyAddress),
    contacts: dto.contacts?.map(mapDTOToContact) ?? [],
    documents: dto.documents?.map(mapDTOToCompanyDocument) ?? [],
    ownerContact: dto.owner_contact ? mapDTOToCompanyContact(dto.owner_contact) : { name: '', contacts: [] },
    financialContact: dto.financial_contact
      ? mapDTOToCompanyContact(dto.financial_contact)
      : { name: '', contacts: [] },
    certificateExpirationDate: dto.certificate_expiration_date
      ? Temporal.PlainDate.from(dto.certificate_expiration_date)
      : undefined,
    internalUser: dto.internal_user ?? undefined,
    contract: dto.contract ? mapDTOToContract(dto.contract) : undefined
  }
}
